// Chunked upload — splits a file into 8MB parts and uploads them one by one to
// our backend, which streams each part directly to Cloudflare R2 as a multipart
// part. Once all parts are received, the backend tells R2 to assemble the final
// object.
//
// Why 8MB?
//   • S3/R2 require every part (except the last) to be >= 5MB, otherwise the
//     final "complete multipart" call rejects with EntityTooSmall.
//   • 8MB is the sweet spot: small enough to fit through ingress proxies, big
//     enough to keep the part count reasonable on large files.
//
// Backend contract (see /app/backend/server.py):
//   POST /api/upload/chunk/init     → { session_id, chunk_size_bytes }
//   POST /api/upload/chunk          → multipart with `file`, query session_id + chunk_index
//   POST /api/upload/chunk/complete → { session_id } → { url, filename }
//   POST /api/upload/chunk/abort    → { session_id }

const CHUNK_SIZE = 8 * 1024 * 1024; // 8MB
const MAX_RETRIES = 5;
const BASE_BACKOFF_MS = 800;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function fingerprintFor(file) {
    return `${file.name}|${file.size}|${file.lastModified || 0}`;
}

function isRetryable(err) {
    if (!err) return false;
    if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED" || err.message === "Network Error") return true;
    const status = err.response?.status;
    if (status == null) return true;
    if (status >= 500) return true;
    if (status === 408 || status === 429) return true;
    return false;
}

/**
 * @param {object} api - axios instance with auth + baseURL=/api
 * @param {File} file - file the user picked
 * @param {(pct:number)=>void} [onProgress]
 * @returns {Promise<{url:string, filename:string}>}
 */
export async function uploadFileChunked(api, file, onProgress) {
    if (!file) throw new Error("Geen bestand opgegeven");
    const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
    const fingerprint = fingerprintFor(file);

    // 1) Init session (also reuses existing in-flight session on reconnect)
    const initResp = await api.post("/upload/chunk/init", {
        filename: file.name,
        total_chunks: totalChunks,
        total_size: file.size,
        fingerprint,
    });
    const sessionId = initResp.data.session_id;
    const alreadyUploaded = new Set(initResp.data.already_uploaded_chunks || []);

    // Track progress per chunk so the bar never moves backwards on retries
    const bytesPerChunk = new Array(totalChunks).fill(0);
    let lastPct = 0;
    for (let i = 0; i < totalChunks; i++) {
        if (alreadyUploaded.has(i)) {
            const start = i * CHUNK_SIZE;
            const end = Math.min(start + CHUNK_SIZE, file.size);
            bytesPerChunk[i] = end - start;
        }
    }
    const report = () => {
        if (!onProgress) return;
        const total = bytesPerChunk.reduce((a, b) => a + b, 0);
        const pct = Math.min(99, Math.round((total / file.size) * 100));
        if (pct > lastPct) {
            lastPct = pct;
            onProgress(pct);
        }
    };
    report();

    // 2) Upload chunks ONE AT A TIME (sequential = stable through residential
    //    connections + proxies; parallel uploads were the cause of the 2%→8%→4%
    //    bouncing progress bar earlier).
    for (let i = 0; i < totalChunks; i++) {
        if (alreadyUploaded.has(i)) continue;
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunkBytes = end - start;
        let lastErr = null;
        let success = false;
        for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
            const fd = new FormData();
            fd.append("file", file.slice(start, end), `chunk_${i}`);
            try {
                await api.post(
                    `/upload/chunk?session_id=${encodeURIComponent(sessionId)}&chunk_index=${i}`,
                    fd,
                    {
                        headers: { "Content-Type": "multipart/form-data" },
                        timeout: 0,
                        maxContentLength: Infinity,
                        maxBodyLength: Infinity,
                        onUploadProgress: (evt) => {
                            // Mid-flight progress for the current chunk
                            const live = Math.min(evt.loaded || 0, chunkBytes);
                            bytesPerChunk[i] = live;
                            report();
                        },
                    },
                );
                bytesPerChunk[i] = chunkBytes;
                report();
                success = true;
                break;
            } catch (err) {
                lastErr = err;
                if (attempt < MAX_RETRIES && isRetryable(err)) {
                    const backoff = Math.min(15000, BASE_BACKOFF_MS * Math.pow(2, attempt));
                    // eslint-disable-next-line no-console
                    console.warn(`[upload] chunk ${i + 1}/${totalChunks} attempt ${attempt + 1} failed, retrying in ${backoff}ms`);
                    // Reset visible progress for this chunk so the next attempt's onUploadProgress events don't double-count
                    bytesPerChunk[i] = 0;
                    await sleep(backoff);
                    continue;
                }
                break;
            }
        }
        if (!success) {
            const status = lastErr?.response?.status;
            const detail = lastErr?.response?.data?.detail;
            const reason = detail || (status ? `HTTP ${status}` : lastErr?.message || "onbekende fout");
            throw new Error(`Upload mislukt op chunk ${i + 1}/${totalChunks}: ${reason}`);
        }
    }

    // 3) Complete — tell R2 to assemble the parts into the final object
    let completeResp = null;
    for (let attempt = 0; attempt <= 3; attempt++) {
        try {
            completeResp = await api.post("/upload/chunk/complete", { session_id: sessionId }, { timeout: 120000 });
            break;
        } catch (err) {
            if (attempt < 3 && isRetryable(err)) {
                await sleep(BASE_BACKOFF_MS * Math.pow(2, attempt));
                continue;
            }
            throw err;
        }
    }
    if (onProgress) onProgress(100);
    return completeResp.data; // { url, filename }
}

/** Explicitly cancel an in-flight upload session (best-effort, never throws). */
export async function cancelChunkedUpload(api, sessionId) {
    if (!sessionId) return;
    try { await api.post("/upload/chunk/abort", { session_id: sessionId }); } catch (_) { /* ignore */ }
}
